import type { ReactNode } from 'react';
import { ParaglideMessage } from '@inlang/paraglide-js-react';
import type { PricingAddon } from '../data/discovery';
import type { DisplayPrice } from '../lib/pricing';
import { chargedPerResult, costLadder } from '../lib/pricing';
import { usdCost } from '../lib/format';
import { billingUnitText } from '../lib/billingUnitText';
import { m } from '../paraglide/messages';

export interface PriceExplainerProps {
  /** matches the trigger's `aria-describedby` */
  id: string;
  price: DisplayPrice;
}

const VALUE = { v: ({ children }: { children?: ReactNode }) => <span className="text-text">{children}</span> };
const BOLD = { b: ({ children }: { children?: ReactNode }) => <b className="font-semibold text-text">{children}</b> };

/**
 * The card behind a metered price: the arithmetic, a ladder of real per-request
 * charges, the one sentence that says what moves the count, and each optional
 * extra the endpoint sells.
 *
 * An optional extra reads as something the endpoint CAN do, not as a second
 * price line.
 *
 * The meter's own story is untouched - arithmetic, ladder, cap sentence - and
 * the extra arrives after it under a hairline, titled by what the money buys
 * rather than by a heading like "add-ons". Its dot is `--c-slate` where the
 * meter's is `--meter`: the same two-pixel state vocabulary the card already
 * uses, now saying "nothing is running here unless you ask". The line under it
 * is the field and the value that turn it on, because a developer reading this
 * card is deciding what to put in the request body.
 *
 * Shown by the parent's hover/focus CSS. Always rendered, never conditional on
 * client state, so prerendered routes hydrate byte-identically.
 */
export const PriceExplainer = ({ id, price }: PriceExplainerProps) => {
  const perResult = chargedPerResult(price.unit);
  const unit = billingUnitText(price.unit ?? '');
  return (
    <span
      id={id}
      role="tooltip"
      className="pointer-events-none invisible absolute top-[calc(100%+9px)] right-0 z-30 w-72 -translate-y-1 rounded-12 border border-line bg-surface p-3.5 text-left opacity-0 shadow-[0_8px_28px_rgba(10,10,10,0.13),0_2px_6px_rgba(10,10,10,0.07)] transition-[opacity,transform,visibility] duration-150 group-hover/price:visible group-hover/price:translate-y-0 group-hover/price:opacity-100 group-focus-visible/price:visible group-focus-visible/price:translate-y-0 group-focus-visible/price:opacity-100"
    >
      <span className="flex items-center gap-2 text-12 font-semibold tracking-[-0.01em]">
        <span className="size-2 shrink-0 rounded-2 bg-meter" />
        {(perResult ? m.measure_explainer_title_returned : m.measure_explainer_title)({ per: unit.per() })}
      </span>
      <span className="mono mt-2 block text-12 leading-[1.65] text-text-2">
        {price.baseUsd === undefined ? null : (
          <ParaglideMessage message={m.measure_explainer_base} inputs={{ amount: usdCost(price.baseUsd) }} markup={VALUE} />
        )}
        <ParaglideMessage
          message={m.measure_explainer_rate}
          inputs={{ amount: usdCost(price.perUnitUsd ?? 0), per: unit.per() }}
          markup={VALUE}
        />
        <br />
        <ParaglideMessage message={m.measure_explainer_cap} inputs={{ amount: usdCost(price.perRequestUsd) }} markup={VALUE} />
      </span>
      <span className="mt-2.5 grid gap-1 border-t border-line-2 pt-2.5">
        {costLadder(price).map((step) => (
          <span
            key={step.items}
            className={`mono flex justify-between text-11 ${step.capped ? 'font-medium text-meter' : 'text-text-2'}`}
          >
            <span className={step.capped ? undefined : 'text-text-3'}>
              {unit.count(step.items)}
            </span>
            <span>
              {step.capped ? m.measure_explainer_step_cap({ amount: usdCost(step.usd) }) : usdCost(step.usd)}
            </span>
          </span>
        ))}
      </span>
      <span className="mt-2.5 block border-t border-line-2 pt-2 text-12 leading-[1.5] text-text-2">
        {perResult ? (
          <ParaglideMessage message={m.measure_explainer_per_result} markup={BOLD} />
        ) : (
          <ParaglideMessage message={m.measure_explainer_per_unit} inputs={{ units: unit.plural() }} markup={BOLD} />
        )}
      </span>
      {price.addons?.map((addon) => <Extra key={addon.id} addon={addon} />)}
    </span>
  );
};

/** One thing the endpoint will also do, priced, and the field that asks for it. */
const Extra = ({ addon }: { addon: PricingAddon }) => (
  <span className="mt-2.5 block border-t border-line-2 pt-2.5">
    <span className="flex items-center justify-between gap-2 text-12 font-semibold tracking-[-0.01em]">
      <span className="flex min-w-0 items-center gap-2">
        <span className="size-2 shrink-0 rounded-2 bg-c-slate" />
        <span className="truncate">{addon.label}</span>
      </span>
      <span className="mono shrink-0 font-medium text-text-2">{usdCost(addon.usd)}</span>
    </span>
    <span className="mono mt-1 block text-11 leading-[1.5] text-text-3">
      <ParaglideMessage message={m.measure_explainer_extra} inputs={{ field: addon.field }} markup={VALUE} />
    </span>
  </span>
);
